// Breadcrumb trail rendered in the topbar. Reads the shell's navigation
// stack and collapses runs of same-page entries so applied filters share
// a single crumb. Clicking a crumb pops the stack back to its first
// occurrence (the unfiltered view of that page).
//
// Calculations: stack collapse keeps the first entry of each consecutive
// run by (entry.id), preserving its original stackIndex for the click
// handler so callers know where to truncate.
//
// Postgres mapping: none, this is session-only UI state.
// Integration hook: none.
import React from 'react';
import { motion } from 'motion/react';
import { Box } from '@mui/material';

const TRAIL_SX = { display: 'flex', alignItems: 'center', gap: 0.75, fontSize: 11 };

// Collapse consecutive stack entries that share the same page id into a
// single breadcrumb crumb. Filter applications keep the page id but add a
// scope context, so without this every applied filter would push a new
// crumb. We keep the FIRST entry of each run so that clicking the crumb
// goes back to the unfiltered version of the page.
function buildBreadcrumb(stack) {
    const out = [];
    stack.forEach((entry, i) => {
        const last = out[out.length - 1];
        if (!last || last.id !== entry.id) {
            out.push({ id: entry.id, label: entry.label, stackIndex: i });
        }
    });
    return out;
}

function isBranchBefore(crumbs, i) {
    if (i === 0) return false;
    return crumbs[i - 1].id.endsWith('-detail');
}

export default function Breadcrumb({ stack, gotoStack }) {
    const crumbs = buildBreadcrumb(stack);
    if (crumbs.length === 0) return null;
    if (crumbs.length === 1) {
        return (
            <Box sx={TRAIL_SX}>
                <Box component="span" sx={{ color: 'var(--muted)', fontWeight: 500 }}>{crumbs[0].label}</Box>
            </Box>
        );
    }
    return (
        <Box sx={TRAIL_SX}>
            {crumbs.map((c, i) => {
                const branch = isBranchBefore(crumbs, i);
                const isLast = i === crumbs.length - 1;
                return (
                    <React.Fragment key={i}>
                        {branch ? (
                            <Box
                                component="span"
                                sx={{ color: 'var(--dim)', fontWeight: 600, letterSpacing: '.025em', userSelect: 'none', mx: 0.25 }}
                            >
                                //
                            </Box>
                        ) : i > 0 ? (
                            <Box component="span" sx={{ color: 'var(--faint)' }}> / </Box>
                        ) : null}
                        {isLast ? (
                            <motion.span
                                style={{ color: 'var(--muted)', fontWeight: 500 }}
                                initial={{ opacity: 0, x: -4 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.2, ease: 'easeOut' }}
                            >
                                {c.label}
                            </motion.span>
                        ) : (
                            <Box
                                component="a"
                                sx={{
                                    color: 'var(--teal)',
                                    cursor: 'pointer',
                                    textDecoration: 'none',
                                    '&:hover': { textDecoration: 'underline' },
                                }}
                                role="button"
                                tabIndex={0}
                                onClick={() => gotoStack(c.stackIndex)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        gotoStack(c.stackIndex);
                                    }
                                }}
                            >
                                {c.label}
                            </Box>
                        )}
                    </React.Fragment>
                );
            })}
        </Box>
    );
}
